import { FLOWS } from 'universal'
import qs from 'query-string'

const STEP_UP_REDIRECT_PATH = '/authflow/entry'
const STEP_UP_AUTH_FLOW_ID = '8ball'
const IS_REDIRECT_FROM_STEPUP = '1'

type StepUpRedirectUrlOptions = {
  /** When true, rs=1 is omitted from returnUri (legacy debitcardnodeweb parity for report-lost). */
  omitResumeFlagFromReturnUri?: boolean
}

function constructStepUpRedirectUrl(
  context,
  flowContext: any = FLOWS.MANAGE,
  returnUriQuery: any = {},
  returnPath?: string,
  options?: StepUpRedirectUrlOptions,
) {
  // as risk is only expecting MANAGE for both flows, we have to map both SUMMARY & MANAGE to MANAGE
  const flowContextMap = {
    [FLOWS.SUMMARY]: FLOWS.MANAGE,
  }

  // Creating clone, to prevent modifying original value of returnUriQuery
  const returnUriQueryEncoded = Object.assign({}, returnUriQuery)

  // authnodeweb perform a double decoding on parameters when the Step Up Auth challenge is completed successfully.
  // anw suggested that we should pass the returnUri double encoded, to prevent receiving incomplete uri.
  // Will follow with that team, when a fix for this will be implemented.
  if ((Object as any).hasOwn(returnUriQueryEncoded, 'returnUri')) {
    returnUriQueryEncoded.returnUri = encodeURIComponent(returnUriQueryEncoded.returnUri)
  }

  const locationQueryObject = {
    ...qs.parse(window.location.search),
    ...returnUriQueryEncoded,
  }
  if (options?.omitResumeFlagFromReturnUri) {
    delete locationQueryObject.rs
  } else {
    locationQueryObject.rs = IS_REDIRECT_FROM_STEPUP
  }
  // Remove cmId in returnUri
  // cmId passed by native should only be used once
  // When user returns from step up auth, we should generate our own cmId
  delete locationQueryObject.cmId
  const locationQuery = qs.stringify(locationQueryObject)

  const queryStringParams = {
    stepupContext: context,
    returnUri:
      (returnPath || window.location.pathname) + (locationQuery ? `?${locationQuery}` : ''),
    flowId: STEP_UP_AUTH_FLOW_ID,
    flowContext: flowContextMap[flowContext] || flowContext,
    'locale.x': locationQueryObject['locale.x'],
  }

  const queryString = qs.stringify(queryStringParams)

  const redirectUrl = `${STEP_UP_REDIRECT_PATH}?${queryString}`

  return redirectUrl
}

function normalizeStepUpContext(context: unknown): unknown {
  return Array.isArray(context) ? context[0] : context
}

function constructStepUpRedirectUrlWithOptions(
  context,
  flowContext: any = FLOWS.MANAGE,
  returnUriQuery: any = {},
  options = {},
) {
  // as risk is only expecting MANAGE for both flows, we have to map both SUMMARY & MANAGE to MANAGE
  const flowContextMap = {
    [FLOWS.SUMMARY]: FLOWS.MANAGE,
  }

  // Creating clone, to prevent modifying original value of returnUriQuery
  const returnUriQueryEncoded = Object.assign({}, returnUriQuery)

  // authnodeweb perform a double decoding on parameters when the Step Up Auth challenge is completed successfully.
  // anw suggested that we should pass the returnUri double encoded, to prevent receiving incomplete uri.
  // Will follow with that team, when a fix for this will be implemented.
  if ((Object as any).hasOwn(returnUriQueryEncoded, 'returnUri')) {
    returnUriQueryEncoded.returnUri = encodeURIComponent(returnUriQueryEncoded.returnUri)
  }

  const locationQueryObject = {
    ...qs.parse(window.location.search),
    ...returnUriQueryEncoded,
    rs: IS_REDIRECT_FROM_STEPUP,
    ...options,
  }
  // Remove cmId in returnUri
  // cmId passed by native should only be used once
  // When user returns from step up auth, we should generate our own cmId
  delete locationQueryObject.cmId
  const locationQuery = qs.stringify(locationQueryObject)

  const queryStringParams = {
    stepupContext: context,
    returnUri: window.location.pathname + (locationQuery ? `?${locationQuery}` : ''),
    flowId: STEP_UP_AUTH_FLOW_ID,
    flowContext: flowContextMap[flowContext] || flowContext,
    'locale.x': locationQueryObject['locale.x'],
  }

  const queryString = qs.stringify(queryStringParams)

  const redirectUrl = `${STEP_UP_REDIRECT_PATH}?${queryString}`

  return redirectUrl
}

function isRedirectFromStepUp(validationParams) {
  if (!validationParams) {
    validationParams = {}
  }

  validationParams = {
    ...validationParams,
    rs: IS_REDIRECT_FROM_STEPUP,
  }

  const queryParams = qs.parse(window.location.search)
  return Object.keys(validationParams).every(
    (paramKey) => String(validationParams[paramKey]) === String(queryParams[paramKey]),
  )
}

export {
  constructStepUpRedirectUrl,
  constructStepUpRedirectUrlWithOptions,
  isRedirectFromStepUp,
  normalizeStepUpContext,
}
